import React, { useEffect, useState } from "react";
import { Trophy, Crown, Medal, Plus, Pencil, Trash2, ShieldCheck, X, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  getLeaderboard,
  adminAddLeader,
  adminUpdateLeader,
  adminDeleteLeader,
} from "../services/leaderboard";
import { Badge } from "../components/common/Card";
import Reveal from "../components/common/Reveal";

export default function Leaderboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Admin modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    country: "IN",
    trades: 250,
    win_rate: 90.0,
    profit: 5000,
    rank: 1,
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchLeaders = async () => {
    try {
      setLoading(true);
      const d = await getLeaderboard();
      setRows(d.leaders || []);
    } catch (err) {
      toast.error("Failed to load leaderboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaders();
  }, []);

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      country: "IN",
      trades: 200,
      win_rate: 88.5,
      profit: 5000,
      rank: rows.length + 1,
    });
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      country: item.country || "IN",
      trades: item.trades,
      win_rate: item.win_rate,
      profit: item.profit ?? item.performance ?? 0,
      rank: item.rank,
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Trader name is required");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formData.name.trim(),
        country: (formData.country || "IN").toUpperCase().trim(),
        trades: parseInt(formData.trades) || 0,
        win_rate: parseFloat(formData.win_rate) || 0,
        profit: parseFloat(formData.profit) || 0,
        performance: parseFloat(formData.profit) || 0,
        rank: parseInt(formData.rank) || 1,
      };

      if (editingItem) {
        await adminUpdateLeader(editingItem.id, payload);
        toast.success(`Updated ${payload.name}`);
      } else {
        await adminAddLeader(payload);
        toast.success(`Added ${payload.name} to leaderboard`);
      }
      setModalOpen(false);
      fetchLeaders();
    } catch (err) {
      const msg = err.response?.data?.detail || "Operation failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name} from the leaderboard?`)) {
      return;
    }
    try {
      setDeletingId(id);
      await adminDeleteLeader(id);
      toast.success(`Removed ${name}`);
      fetchLeaders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete trader");
    } finally {
      setDeletingId(null);
    }
  };

  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);
  const order = [1, 0, 2]; // podium visual order

  return (
    <div className="mx-auto max-w-5xl pb-10">
      <Reveal>
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500">
              <Trophy size={14} /> Community
            </div>
            <h1 className="font-head text-2xl font-bold uppercase tracking-tight text-white md:text-3xl">
              Top 10 Traders
            </h1>
          </div>

          <div className="flex items-center gap-2.5">
            {isAdmin && (
              <>
                <Badge tone="verified" className="flex items-center gap-1">
                  <ShieldCheck size={12} /> Admin Mode
                </Badge>
                <button
                  onClick={openAddModal}
                  className="btn-silver flex items-center gap-1.5 rounded-lg px-3.5 py-2 font-mono text-xs font-bold uppercase tracking-wider"
                  data-testid="admin-add-leader-btn"
                >
                  <Plus size={14} /> Add Trader
                </button>
              </>
            )}
            {!isAdmin && <Badge tone="demo">Verified Data</Badge>}
          </div>
        </div>
      </Reveal>

      {/* Podium */}
      <div className="mt-8 grid grid-cols-3 items-end gap-3 md:gap-5">
        {order.map((idx, pos) => {
          const t = top3[idx];
          if (!t) return <div key={pos} />;
          const heights = ["mt-6", "", "mt-10"];
          const medal = ["#c0c4c8", "#e6c877", "#c69a6b"];
          const val = Number(t.profit ?? t.performance ?? 0);

          return (
            <Reveal key={t.id || t.rank} delay={pos * 0.08}>
              <div
                className={`surface relative group rounded-2xl p-5 text-center transition-all ${heights[pos]}`}
                data-testid="leaderboard-podium"
              >
                {isAdmin && (
                  <div className="absolute right-2.5 top-2.5 flex items-center gap-1 opacity-80 transition-opacity group-hover:opacity-100">
                    <button
                      onClick={() => openEditModal(t)}
                      title="Edit Trader"
                      className="grid h-7 w-7 place-items-center rounded-md border border-white/10 bg-black/60 text-neutral-300 hover:border-white/30 hover:text-white"
                      data-testid={`admin-edit-podium-${t.rank}`}
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => handleDelete(t.id, t.name)}
                      disabled={deletingId === t.id}
                      title="Delete Trader"
                      className="grid h-7 w-7 place-items-center rounded-md border border-rose-500/20 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 hover:text-rose-200"
                      data-testid={`admin-delete-podium-${t.rank}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}

                <span
                  className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full border"
                  style={{ borderColor: `${medal[idx]}55`, color: medal[idx] }}
                >
                  {idx === 0 ? <Crown size={18} /> : <Medal size={16} />}
                </span>
                <div className="font-head text-3xl font-black" style={{ color: medal[idx] }}>
                  #{t.rank}
                </div>
                <div className="mt-2 text-sm font-medium text-white">{t.name}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  {t.win_rate}% win · {t.trades} trades
                </div>
                <div className="mt-3 font-mono text-sm font-semibold tabular-nums text-[#8fd6b4]">
                  ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>

      {/* Remaining rows */}
      <Reveal delay={0.1}>
        <div className="surface mt-6 overflow-hidden rounded-2xl">
          <div className="grid grid-cols-12 border-b border-white/8 px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            <div className="col-span-1">#</div>
            <div className={isAdmin ? "col-span-4" : "col-span-5"}>Trader</div>
            <div className="col-span-2 text-right">Trades</div>
            <div className="col-span-2 text-right">Win %</div>
            <div className={isAdmin ? "col-span-2 text-right" : "col-span-3 text-right"}>Profit</div>
            {isAdmin && <div className="col-span-1 text-right">Actions</div>}
          </div>

          {rest.map((r) => {
            const val = Number(r.profit ?? r.performance ?? 0);
            return (
              <div
                key={r.id || r.rank}
                className="grid grid-cols-12 items-center border-b border-white/5 px-5 py-3.5 transition-colors hover:bg-white/[0.02]"
                data-testid="leaderboard-row"
              >
                <div className="col-span-1 font-head text-sm font-bold text-neutral-500">{r.rank}</div>
                <div className={`${isAdmin ? "col-span-4" : "col-span-5"} flex items-center gap-3`}>
                  <span className="grid h-8 w-8 place-items-center rounded-full border border-white/10 font-mono text-[10px] text-neutral-400">
                    {r.name.split(" ").map((n) => n[0]).join("")}
                  </span>
                  <span className="text-sm text-neutral-200">{r.name}</span>
                  {r.country && (
                    <span className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[9px] text-neutral-500">
                      {r.country}
                    </span>
                  )}
                </div>
                <div className="col-span-2 text-right font-mono text-sm tabular-nums text-neutral-400">
                  {r.trades}
                </div>
                <div className="col-span-2 text-right font-mono text-sm tabular-nums text-neutral-200">
                  {r.win_rate}%
                </div>
                <div className={`${isAdmin ? "col-span-2" : "col-span-3"} text-right font-mono text-sm font-semibold tabular-nums text-[#8fd6b4]`}>
                  ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>

                {isAdmin && (
                  <div className="col-span-1 flex justify-end gap-1.5">
                    <button
                      onClick={() => openEditModal(r)}
                      title="Edit"
                      className="grid h-7 w-7 place-items-center rounded-md border border-white/10 text-neutral-400 hover:border-white/30 hover:text-white"
                      data-testid={`admin-edit-row-${r.rank}`}
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => handleDelete(r.id, r.name)}
                      disabled={deletingId === r.id}
                      title="Delete"
                      className="grid h-7 w-7 place-items-center rounded-md border border-rose-500/20 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300"
                      data-testid={`admin-delete-row-${r.rank}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Reveal>

      <p className="mt-6 text-center font-mono text-[11px] text-neutral-600">
        Verified trader performance records. Central synchronized platform ranking.
      </p>

      {/* Admin Add/Edit Modal */}
      {isAdmin && modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <div className="surface w-full max-w-md rounded-2xl border border-white/15 p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-[#8fd6b4]" />
                <h3 className="font-head text-base font-bold uppercase tracking-tight text-white">
                  {editingItem ? `Edit Trader #${editingItem.rank}` : "Add Leaderboard Trader"}
                </h3>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-1 text-neutral-400 hover:bg-white/5 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave} className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-neutral-400">
                  Trader Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Aarav M."
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-2.5 font-mono text-sm text-white outline-none focus:border-white/30"
                  data-testid="leaderboard-form-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-neutral-400">
                    Country Code
                  </label>
                  <input
                    type="text"
                    maxLength={3}
                    placeholder="e.g. IN, BR, US"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value.toUpperCase() })}
                    className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-2.5 font-mono text-sm text-white outline-none focus:border-white/30"
                    data-testid="leaderboard-form-country"
                  />
                </div>

                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-neutral-400">
                    Rank Position
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={formData.rank}
                    onChange={(e) => setFormData({ ...formData, rank: e.target.value })}
                    className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-2.5 font-mono text-sm text-white outline-none focus:border-white/30"
                    data-testid="leaderboard-form-rank"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-neutral-400">
                    Trades
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={formData.trades}
                    onChange={(e) => setFormData({ ...formData, trades: e.target.value })}
                    className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-white/30"
                    data-testid="leaderboard-form-trades"
                  />
                </div>

                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-neutral-400">
                    Win %
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min={0}
                    max={100}
                    value={formData.win_rate}
                    onChange={(e) => setFormData({ ...formData, win_rate: e.target.value })}
                    className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-white/30"
                    data-testid="leaderboard-form-winrate"
                  />
                </div>

                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-neutral-400">
                    Profit ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.profit}
                    onChange={(e) => setFormData({ ...formData, profit: e.target.value })}
                    className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 font-mono text-sm text-[#8fd6b4] outline-none focus:border-[#8fd6b4]/50"
                    data-testid="leaderboard-form-profit"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="rounded-lg border border-white/10 px-4 py-2 font-mono text-xs text-neutral-300 hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-silver flex items-center gap-1.5 rounded-lg px-5 py-2 font-mono text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                  data-testid="leaderboard-form-submit"
                >
                  <Check size={14} /> {saving ? "Saving..." : editingItem ? "Update Trader" : "Add Trader"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

